import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError, inr, API } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Plus, ArrowsClockwise, PencilSimple, Eye, Hammer, DownloadSimple, UploadSimple } from "@phosphor-icons/react";

const ENTITY_TYPES = [
    { value: "individual", label: "Individual (TDS @ 1% u/s 194C)" },
    { value: "company", label: "Company / HUF / Firm (TDS @ 2% u/s 194C)" },
];

const emptyForm = {
    worker_code: "", name: "", entity_type: "individual",
    pan: "", gstin: "", phone: "", address: "",
    bank_account_number: "", ifsc_code: "", account_holder_name: "", notes: "",
};

export default function ContractWorkers() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const isFounder = user?.role === "founder";
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showInactive, setShowInactive] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [ratesFor, setRatesFor] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/contract-workers", { params: { active_only: !showInactive } });
            setItems(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [showInactive]);
    useEffect(() => { load(); }, [load]);

    const openNew = () => { setEditing(null); setDialogOpen(true); };
    const openEdit = (w) => { setEditing(w); setDialogOpen(true); };

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title="Contract workers"
                sub="Piece-rate labour paid per unit produced. TDS is auto-computed at 1% (individual) or 2% (company) under section 194C."
                right={
                    <div className="flex items-center gap-2">
                        <label className="text-xs flex items-center gap-1">
                            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="cw-show-inactive" />
                            Show inactive
                        </label>
                        <Button variant="outline" onClick={load} disabled={loading} data-testid="cw-refresh">
                            <ArrowsClockwise size={14} className="mr-2" /> Refresh
                        </Button>
                        {isFounder ? (
                            <>
                                <Button variant="outline" onClick={() => { window.location.href = `${API}/bulk-import/contract-workers/template`; }} data-testid="cw-template">
                                    <DownloadSimple size={14} className="mr-2" /> Template
                                </Button>
                                <Button variant="outline" onClick={() => navigate("/bulk-imports")} data-testid="cw-bulk-upload">
                                    <UploadSimple size={14} className="mr-2" /> Bulk upload
                                </Button>
                            </>
                        ) : null}
                        <Button onClick={openNew} style={{ background: "#f59e0b", color: "#fff" }} data-testid="cw-new">
                            <Plus size={14} className="mr-2" /> New worker
                        </Button>
                    </div>
                } />

            <div className="p-8 space-y-6">
                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    <div className="px-5 py-3 border-b font-serif text-lg" style={{ borderColor: "var(--se-border)" }}>
                        {items.length} worker{items.length === 1 ? "" : "s"}
                    </div>
                    <table className="se-table" data-testid="cw-table">
                        <thead>
                            <tr>
                                <th>Code</th><th>Name</th><th>Entity</th><th>PAN / GSTIN</th>
                                <th>Phone</th><th className="text-right">TDS %</th><th>Status</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 ? (
                                <tr><td colSpan="8" className="py-10 text-center text-stone-500">
                                    No workers yet. Click &quot;New worker&quot; to add one.
                                </td></tr>
                            ) : items.map((w) => (
                                <tr key={w.id} data-testid={`cw-row-${w.id}`}>
                                    <td className="font-mono text-xs">{w.worker_code}</td>
                                    <td>{w.name}</td>
                                    <td className="text-xs capitalize">{w.entity_type}</td>
                                    <td className="text-xs">{w.pan || w.gstin || "—"}</td>
                                    <td className="text-xs">{w.phone || "—"}</td>
                                    <td className="se-num text-right text-xs">{w.entity_type === "individual" ? "1%" : "2%"}</td>
                                    <td className="text-xs">{w.active ? <span className="text-emerald-700">Active</span> : <span className="text-stone-500">Inactive</span>}</td>
                                    <td className="text-right">
                                        <Button size="sm" variant="outline" onClick={() => setRatesFor(w)} data-testid={`cw-rates-${w.id}`} className="mr-1">
                                            <Eye size={13} className="mr-1" /> Rates
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => openEdit(w)} data-testid={`cw-edit-${w.id}`}>
                                            <PencilSimple size={13} className="mr-1" /> Edit
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="text-xs text-stone-500 border rounded-md p-4" style={{ borderColor: "var(--se-border)", background: "#fefce8" }}>
                    <div className="font-semibold text-stone-700 mb-1"><Hammer size={13} className="inline mr-1" /> How this works</div>
                    Each month, upload one Excel file with everyone&apos;s work: Worker Code · Product · Qty · Rate. The system remembers the last-approved rate per (worker × product). Any change on next month&apos;s upload is flagged for founder confirmation before it becomes the new learned rate.
                </div>
            </div>

            <WorkerDialog open={dialogOpen} onOpenChange={setDialogOpen} editing={editing} onSaved={load} />
            <PieceRatesDialog worker={ratesFor} onClose={() => setRatesFor(null)} />
        </>
    );
}

function WorkerDialog({ open, onOpenChange, editing, onSaved }) {
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (open) {
            setForm(editing ? { ...emptyForm, ...editing } : emptyForm);
        }
    }, [open, editing]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async () => {
        if (!form.name.trim()) return toast.error("Name is required");
        setSaving(true);
        try {
            if (editing) {
                await api.patch(`/contract-workers/${editing.id}`, form);
                toast.success("Worker updated");
            } else {
                await api.post("/contract-workers", form);
                toast.success("Worker added");
            }
            onOpenChange(false);
            onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid="cw-dialog">
                <DialogHeader><DialogTitle>{editing ? "Edit worker" : "New contract worker"}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Worker code</Label>
                            <Input value={form.worker_code || ""} onChange={(e) => set("worker_code", e.target.value)}
                                   placeholder="Auto (CW-###) if blank" data-testid="cw-form-code" /></div>
                        <div><Label>Name *</Label>
                            <Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="cw-form-name" /></div>
                    </div>
                    <div><Label>Entity type *</Label>
                        <select className="w-full border rounded-sm px-3 py-2 text-sm" value={form.entity_type}
                                onChange={(e) => set("entity_type", e.target.value)} data-testid="cw-form-entity">
                            {ENTITY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>PAN</Label>
                            <Input value={form.pan || ""} onChange={(e) => set("pan", e.target.value.toUpperCase())} data-testid="cw-form-pan" /></div>
                        <div><Label>GSTIN</Label>
                            <Input value={form.gstin || ""} onChange={(e) => set("gstin", e.target.value.toUpperCase())} data-testid="cw-form-gstin" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Phone</Label>
                            <Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="cw-form-phone" /></div>
                        <div><Label>Address</Label>
                            <Input value={form.address || ""} onChange={(e) => set("address", e.target.value)} data-testid="cw-form-address" /></div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div><Label>Bank a/c no</Label>
                            <Input value={form.bank_account_number || ""} onChange={(e) => set("bank_account_number", e.target.value)} data-testid="cw-form-ac" /></div>
                        <div><Label>IFSC</Label>
                            <Input value={form.ifsc_code || ""} onChange={(e) => set("ifsc_code", e.target.value.toUpperCase())} data-testid="cw-form-ifsc" /></div>
                        <div><Label>A/c holder</Label>
                            <Input value={form.account_holder_name || ""} onChange={(e) => set("account_holder_name", e.target.value)} data-testid="cw-form-holder" /></div>
                    </div>
                    <div><Label>Notes</Label>
                        <textarea className="w-full border rounded-sm px-3 py-2 text-sm min-h-16"
                                  value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="cw-form-notes" /></div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} style={{ background: "#f59e0b", color: "#fff" }} data-testid="cw-form-save">
                        {saving ? "Saving..." : "Save worker"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function PieceRatesDialog({ worker, onClose }) {
    const [rates, setRates] = useState([]);
    const [history, setHistory] = useState([]);
    useEffect(() => {
        if (!worker) return;
        (async () => {
            try {
                const [r, h] = await Promise.all([
                    api.get("/piece-rates", { params: { worker_id: worker.id } }),
                    api.get("/piece-rates/history", { params: { worker_id: worker.id } }),
                ]);
                setRates(r.data);
                setHistory(h.data);
            } catch (e) { toast.error(formatApiError(e)); }
        })();
    }, [worker]);

    return (
        <Dialog open={!!worker} onOpenChange={(o) => { if (!o) onClose && onClose(); }}>
            <DialogContent className="max-w-3xl" data-testid="cw-rates-dialog">
                <DialogHeader><DialogTitle>Learned piece rates — {worker?.name}</DialogTitle></DialogHeader>
                <div className="space-y-6">
                    <div>
                        <div className="text-xs font-semibold text-stone-600 mb-2">Current learned rates ({rates.length})</div>
                        {rates.length === 0 ? (
                            <div className="text-xs text-stone-500 border rounded-md p-4 text-center" style={{ borderColor: "var(--se-border)" }}>
                                No rates learned yet. Rates get learned when a founder approves a payout run.
                            </div>
                        ) : (
                            <table className="se-table w-full">
                                <thead><tr><th>Product</th><th className="text-right">Rate (₹)</th><th>Last updated</th></tr></thead>
                                <tbody>{rates.map((r, i) => (
                                    <tr key={i}>
                                        <td>{r.product}</td>
                                        <td className="se-num text-right">₹ {inr(r.rate)}</td>
                                        <td className="text-xs text-stone-500">{r.updated_at?.slice(0, 10)}</td>
                                    </tr>
                                ))}</tbody>
                            </table>
                        )}
                    </div>
                    {history.length > 0 ? (
                        <div>
                            <div className="text-xs font-semibold text-stone-600 mb-2">Rate history ({history.length})</div>
                            <table className="se-table w-full">
                                <thead><tr><th>Product</th><th className="text-right">Old → New</th><th>When</th></tr></thead>
                                <tbody>{history.slice(0, 20).map((h, i) => (
                                    <tr key={i}>
                                        <td>{h.product}</td>
                                        <td className="se-num text-right text-xs">
                                            {h.old_rate != null ? <span className="text-stone-500">₹{inr(h.old_rate)} → </span> : null}
                                            <span className="font-medium">₹{inr(h.new_rate)}</span>
                                        </td>
                                        <td className="text-xs text-stone-500">{h.changed_at?.slice(0, 10)}</td>
                                    </tr>
                                ))}</tbody>
                            </table>
                        </div>
                    ) : null}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
